import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import React, { useState } from 'react';

import { nextBusinessDay, startOfDay } from './dates';
import { downloadIcs } from './downloadIcs';
import {
  buildGoogleCalendarUrl,
  buildIcsCalendar,
  calendarEventFromStep,
} from './ics';
import { type CalendarStep } from './steps';
import styles from '../DevelopmentPlan.module.scss';

const MANUAL_UPDATE_NOTE =
  'Heldag med påminnelse kl. 09. Kalenderen oppdateres ikke av seg selv hvis teksten endres. Legg steget inn på nytt.';

type AddStepToCalendarProps = {
  step: CalendarStep;
  planUrl: string;
  defaultDate: Date | null;
  onPlaced: (date: Date) => void;
};

export const AddStepToCalendar: React.FC<AddStepToCalendarProps> = ({
  step,
  planUrl,
  defaultDate,
  onPlaced,
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date>(() => nextBusinessDay(new Date()));
  const [notice, setNotice] = useState<string | null>(null);

  const event = calendarEventFromStep(step, date, planUrl);
  const googleUrl = buildGoogleCalendarUrl(event);

  const openPanel = () => {
    setDate(startOfDay(defaultDate ?? nextBusinessDay(new Date())));
    setNotice(null);
    setOpen(true);
  };

  const addToGoogle = () => {
    const opened = window.open(googleUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      setNotice('Nettleseren blokkerte fanen. Kopier lenken i stedet.');
      return;
    }
    setNotice('Google Kalender åpnes i en ny fane.');
    onPlaced(date);
  };

  const downloadCalendarFile = () => {
    downloadIcs('utviklingsplan.ics', buildIcsCalendar([event], new Date()));
    setNotice('Kalenderfilen er lastet ned.');
    onPlaced(date);
  };

  const copyGoogleLink = async () => {
    try {
      await navigator.clipboard.writeText(googleUrl);
      setNotice('Lenke kopiert.');
    } catch {
      setNotice('Klarte ikke å kopiere lenken.');
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.calendarIconButton}
        aria-label={`Legg ${step.title} i kalenderen`}
        onClick={openPanel}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{step.title}</DialogTitle>
        <DialogContent>
          <Stack
            spacing={1.5}
            className={styles.calendarActions}
            sx={{ pt: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: '#374151' }}>
              {step.text}
            </Typography>
            <DatePicker
              label="Dag"
              value={moment(date)}
              format="DD.MM.YYYY"
              onChange={(value: Moment | null) => {
                if (value?.isValid()) {
                  setDate(value.clone().startOf('day').toDate());
                }
              }}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <Typography variant="body2" sx={{ color: '#4b5563' }}>
              {MANUAL_UPDATE_NOTE}
            </Typography>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={addToGoogle}
            >
              Legg til i Google Kalender
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={downloadCalendarFile}
            >
              Legg til i kalender
            </button>
            <button
              type="button"
              className={styles.calendarLinkButton}
              onClick={() => void copyGoogleLink()}
            >
              Kopier Google-lenke
            </button>
            {notice && (
              <Typography variant="body2" sx={{ color: '#111827' }}>
                {notice}
              </Typography>
            )}
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setOpen(false)}
            >
              Lukk
            </button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
